import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Loader2, Copy, Save, RefreshCcw, FileText, Linkedin,
  ClipboardCheck, BookOpen, Check, History, ChevronLeft, ChevronRight,
  ShieldCheck, X, MessageSquare, Camera, Trophy, Star, Type,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api, pdfUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KINDS = [
  {
    key: "case_study",
    label: "Case Study PDF",
    hint: "The one-page A4 case study — hero, key facts, gallery, R&C branding.",
    icon: BookOpen,
    accent: "from-amber-400/20 to-amber-500/5",
    external: true,
  },
  {
    key: "article",
    label: "Website Article",
    hint: "Full article package (400–650 words) plus meta title, description, slug, keyphrase and tags.",
    icon: FileText,
    accent: "from-sky-400/20 to-sky-500/5",
    limit: null,
  },
  {
    key: "linkedin",
    label: "LinkedIn — Company Post",
    hint: "Professional company-page post: opening line, achievement, facts, closing CTA.",
    icon: Linkedin,
    accent: "from-indigo-400/20 to-indigo-500/5",
    limit: 3000,
  },
  {
    key: "linkedin_personal",
    label: "LinkedIn — Personal Post",
    hint: "Natural first-person version for team members to share.",
    icon: Linkedin,
    accent: "from-blue-400/20 to-blue-500/5",
    limit: 3000,
  },
  {
    key: "facebook",
    label: "Facebook Post",
    hint: "Clear, accessible summary in everyday language.",
    icon: MessageSquare,
    accent: "from-cyan-400/20 to-cyan-500/5",
    limit: 3000,
  },
  {
    key: "instagram",
    label: "Instagram Caption",
    hint: "Short, visual-first caption to pair with a site photo.",
    icon: Camera,
    accent: "from-pink-400/20 to-pink-500/5",
    limit: 2200,
  },
  {
    key: "short_caption",
    label: "Short Social Caption",
    hint: "One reusable caption (max 240 chars) for any channel.",
    icon: MessageSquare,
    accent: "from-teal-400/20 to-teal-500/5",
    limit: 280,
  },
  {
    key: "bid",
    label: "Bid Evidence Pack",
    hint: "Summary + 250-word + 500-word responses, key facts and relevant experience.",
    icon: ClipboardCheck,
    accent: "from-emerald-400/20 to-emerald-500/5",
    limit: null,
  },
  {
    key: "award",
    label: "Award Entry Evidence",
    hint: "Structured award entry: categories, background, innovation, impact and evidence checklist.",
    icon: Trophy,
    accent: "from-orange-400/20 to-orange-500/5",
    limit: null,
  },
];

const SECTIONS = [
  { title: "Website Article", keys: ["article"] },
  { title: "Social Content", keys: ["linkedin", "linkedin_personal", "facebook", "instagram", "short_caption"] },
  { title: "Bid & Award Evidence", keys: ["bid", "award"] },
];

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-gray-100 text-gray-700" },
  in_review: { label: "In Review", cls: "bg-sky-100 text-sky-700" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
  exported: { label: "Exported", cls: "bg-violet-100 text-violet-700" },
  published: { label: "Published", cls: "bg-amber-100 text-amber-800" },
  archived: { label: "Archived", cls: "bg-gray-200 text-gray-500" },
};

const MANUAL_STATUSES = ["draft", "in_review", "exported", "published", "archived"];

function fmt(dt) {
  return dt ? new Date(dt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : null;
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-semibold ${meta.cls}`} data-testid="output-status-badge">
      {meta.label}
    </span>
  );
}

function StatusSelect({ value, onChange, disabled }) {
  return (
    <select
      value={MANUAL_STATUSES.includes(value) ? value : ""}
      onChange={(e) => e.target.value && onChange(e.target.value)}
      disabled={disabled}
      className="rounded-full border border-gray-300 bg-white text-xs font-semibold px-3 py-2 hover:border-amber-300 disabled:opacity-40 cursor-pointer"
      data-testid="output-status-select"
    >
      <option value="">Set status…</option>
      {MANUAL_STATUSES.map((s) => (
        <option key={s} value={s}>{STATUS_META[s].label}</option>
      ))}
    </select>
  );
}

function MetaRow({ state }) {
  const bits = [];
  if (state?.last_generated_at) bits.push(`Generated ${fmt(state.last_generated_at)}`);
  if (state?.last_edited_at) bits.push(`Edited ${fmt(state.last_edited_at)}${state.last_edited_by ? ` by ${state.last_edited_by}` : ""}`);
  if (state?.approved_at) bits.push(`Approved ${fmt(state.approved_at)}${state.approved_by ? ` by ${state.approved_by}` : ""}`);
  if (!bits.length) return null;
  return <div className="mt-2 text-[11px] text-gray-500" data-testid="output-meta-row">{bits.join(" · ")}</div>;
}

function VersionHistoryModal({ kind, versions, state, onClose, onView, onRestore, onApprove, isAdmin }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid={`version-history-${kind.key}`}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display font-semibold text-lg text-gray-900">Version history</div>
            <div className="text-xs text-gray-500">{kind.label} · {versions.length} version{versions.length !== 1 ? "s" : ""}. Nothing is ever deleted.</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" data-testid="version-history-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3">
          {versions.map((v) => {
            const isCurrent = state.current_version === v.version;
            const isApproved = state.approved_version === v.version;
            return (
              <div key={v.version} className={`rounded-xl border p-3 ${isCurrent ? "border-amber-400 bg-amber-50/50" : "border-gray-200"}`} data-testid={`version-row-${v.version}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">v{v.version}</span>
                    <span className="text-xs text-gray-500">{v.source}{v.restored_from ? ` (from v${v.restored_from})` : ""}</span>
                    {isCurrent && <span className="text-[10px] uppercase tracking-wider bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-semibold">Active</span>}
                    {isApproved && <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">Approved</span>}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {fmt(v.created_at)}{v.created_by ? ` · ${v.created_by}` : ""}
                    {v.edited_at ? ` · edited ${fmt(v.edited_at)}` : ""}
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-600 line-clamp-2 whitespace-pre-wrap">{(v.text || "").slice(0, 220)}</div>
                <div className="mt-2 flex gap-2">
                  <button onClick={() => onView(v)} className="text-xs font-semibold text-gray-700 hover:text-black underline" data-testid={`version-view-${v.version}`}>View</button>
                  {!isCurrent && (
                    <button onClick={() => onRestore(v.version)} className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline" data-testid={`version-restore-${v.version}`}>Restore</button>
                  )}
                  {isAdmin && !isApproved && (
                    <button onClick={() => onApprove(v.version)} className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline" data-testid={`version-approve-${v.version}`}>Approve</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function OutputCard({ projectId, kind, state, onState, isAdmin }) {
  const [text, setText] = useState(state?.current_text || "");
  const [viewedVersion, setViewedVersion] = useState(state?.current_version || 0);
  const [versions, setVersions] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setText(state?.current_text || "");
    setViewedVersion(state?.current_version || 0);
  }, [state?.current_version, state?.current_text]);

  const loadVersions = async () => {
    const { data } = await api.get(`/admin/projects/${projectId}/outputs/${kind.key}/versions`);
    setVersions(data.versions);
    return data.versions;
  };

  const generate = async () => {
    const total = state?.total_versions || 0;
    if (total > 0 && !window.confirm("Generate a new version?\n\nYour current version will be kept. You can return to it at any time.")) return;
    setBusy("gen");
    try {
      const { data } = await api.post(`/admin/projects/${projectId}/outputs/${kind.key}`);
      onState(kind.key, { ...data.state, current_text: data.text });
      setVersions(null);
      toast.success(`${kind.label} v${data.version} generated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.patch(`/admin/projects/${projectId}/outputs/${kind.key}/versions/${viewedVersion}`, { text });
      onState(kind.key, { ...data.state, current_text: data.version === data.state.current_version ? data.text : state?.current_text });
      setViewedVersion(data.version);
      setVersions(null);
      toast.success(data.version !== viewedVersion ? `Saved as new version v${data.version} (approved versions are never overwritten)` : "Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy("");
    }
  };

  const approve = async (version) => {
    const notes = window.prompt("Approval notes (optional):", "") ?? null;
    if (notes === null) return;
    setBusy("approve");
    try {
      const { data } = await api.post(`/admin/projects/${projectId}/outputs/${kind.key}/versions/${version}/approve`, { notes });
      onState(kind.key, { ...data.state, current_text: text });
      setShowHistory(false);
      toast.success(`v${version} approved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy("");
    }
  };

  const restore = async (version) => {
    setBusy("restore");
    try {
      const { data } = await api.post(`/admin/projects/${projectId}/outputs/${kind.key}/versions/${version}/restore`);
      const list = versions || (await loadVersions());
      const doc = list.find((v) => v.version === version);
      onState(kind.key, { ...data.state, current_text: doc?.text || "" });
      setShowHistory(false);
      toast.success(`v${version} is now the active version`);
    } catch (e) {
      toast.error("Restore failed");
    } finally {
      setBusy("");
    }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await api.post(`/admin/projects/${projectId}/outputs/${kind.key}/status`, { status });
      onState(kind.key, { ...data.state, current_text: state?.current_text });
      toast.success(`Status: ${STATUS_META[status].label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed");
    }
  };

  const nav = async (dir) => {
    const list = versions || (await loadVersions());
    const target = viewedVersion + dir;
    const doc = list.find((v) => v.version === target);
    if (!doc) return;
    setViewedVersion(target);
    setText(doc.text || "");
  };

  const openHistory = async () => {
    await loadVersions();
    setShowHistory(true);
  };

  const viewVersion = (v) => {
    setViewedVersion(v.version);
    setText(v.text || "");
    setShowHistory(false);
  };

  const copy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const Icon = kind.icon;
  const total = state?.total_versions || 0;
  const generated = total > 0;
  const chars = text.length;
  const over = kind.limit ? chars > kind.limit : false;

  return (
    <div className={`rounded-2xl border border-gray-200 bg-gradient-to-br ${kind.accent} p-5 sm:p-6 flex flex-col`} data-testid={`output-card-${kind.key}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/70 backdrop-blur inline-flex items-center justify-center border border-white/60">
            <Icon className="h-5 w-5 text-gray-800" />
          </div>
          <div>
            <div className="font-display font-semibold text-gray-900">{kind.label}</div>
            <div className="text-xs text-gray-600 mt-0.5 max-w-md">{kind.hint}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={generated ? state?.status : "draft"} />
          {generated && (
            <span className="text-[11px] text-gray-500" data-testid={`output-version-label-${kind.key}`}>
              Version {viewedVersion} of {total}
              {state?.approved_version ? ` · Approved: v${state.approved_version}` : ""}
            </span>
          )}
        </div>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={generated ? "" : "Not generated yet — click Generate to draft with AI."}
        className={`mt-4 min-h-[200px] w-full rounded-xl border bg-white/80 backdrop-blur p-3 text-sm text-gray-900 leading-relaxed font-mono resize-y ${over ? "border-red-400 ring-1 ring-red-300" : "border-gray-200 focus:border-amber-400"} focus:outline-none focus:ring-2 focus:ring-amber-200/70`}
        data-testid={`output-textarea-${kind.key}`}
      />

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
        <span>{chars.toLocaleString()} chars</span>
        {kind.limit && <span className={over ? "text-red-600 font-semibold" : ""}>· limit {kind.limit.toLocaleString()}</span>}
        {generated && total > 1 && (
          <span className="ml-auto inline-flex items-center gap-1">
            <button onClick={() => nav(-1)} disabled={viewedVersion <= 1} className="p-1 rounded hover:bg-white/70 disabled:opacity-30" data-testid={`output-prev-${kind.key}`}>
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={() => nav(1)} disabled={viewedVersion >= total} className="p-1 rounded hover:bg-white/70 disabled:opacity-30" data-testid={`output-next-${kind.key}`}>
              <ChevronRight className="h-4 w-4" />
            </button>
          </span>
        )}
      </div>
      <MetaRow state={state} />

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={generate} disabled={busy === "gen"} className="inline-flex items-center gap-2 rounded-full bg-gray-900 text-white text-xs font-semibold px-4 py-2 hover:bg-black disabled:opacity-60" data-testid={`output-generate-${kind.key}`}>
          {busy === "gen" ? <Loader2 className="h-4 w-4 animate-spin" /> : (generated ? <RefreshCcw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />)}
          {generated ? "Generate New Version" : "Generate"}
        </button>
        {generated && (
          <button onClick={save} disabled={busy === "save"} className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-gray-900 text-xs font-semibold px-4 py-2 hover:bg-amber-300 disabled:opacity-40" data-testid={`output-save-${kind.key}`}>
            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
        )}
        <button onClick={copy} disabled={!text} className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white text-xs font-semibold px-4 py-2 hover:border-amber-300 disabled:opacity-40" data-testid={`output-copy-${kind.key}`}>
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy"}
        </button>
        {generated && (
          <button onClick={openHistory} className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white text-xs font-semibold px-4 py-2 hover:border-amber-300" data-testid={`output-history-${kind.key}`}>
            <History className="h-4 w-4" /> History
          </button>
        )}
        {generated && isAdmin && state?.approved_version !== viewedVersion && (
          <button onClick={() => approve(viewedVersion)} disabled={busy === "approve"} className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white text-xs font-semibold px-4 py-2 hover:bg-emerald-700 disabled:opacity-60" data-testid={`output-approve-${kind.key}`}>
            <ShieldCheck className="h-4 w-4" /> Approve v{viewedVersion}
          </button>
        )}
        {generated && <StatusSelect value={state?.status} onChange={setStatus} />}
      </div>

      {showHistory && versions && (
        <VersionHistoryModal
          kind={kind}
          versions={versions}
          state={state}
          onClose={() => setShowHistory(false)}
          onView={viewVersion}
          onRestore={restore}
          onApprove={approve}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}

function CaseStudyCard({ project, state, onState, isAdmin }) {
  const kind = KINDS[0];
  const Icon = kind.icon;
  const [busy, setBusy] = useState("");

  const approve = async () => {
    const notes = window.prompt("Approval notes (optional):", "") ?? null;
    if (notes === null) return;
    setBusy("approve");
    try {
      const { data } = await api.post(`/admin/projects/${project.id}/outputs/case_study/versions/0/approve`, { notes });
      onState("case_study", data.state);
      toast.success("Case study approved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy("");
    }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await api.post(`/admin/projects/${project.id}/outputs/case_study/status`, { status });
      onState("case_study", data.state);
      toast.success(`Status: ${STATUS_META[status].label}`);
    } catch {
      toast.error("Status change failed");
    }
  };

  return (
    <div className={`rounded-2xl border border-gray-200 bg-gradient-to-br ${kind.accent} p-5 sm:p-6 flex flex-col`} data-testid="output-card-case_study">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/70 backdrop-blur inline-flex items-center justify-center border border-white/60">
            <Icon className="h-5 w-5 text-gray-800" />
          </div>
          <div>
            <div className="font-display font-semibold text-gray-900">{kind.label}</div>
            <div className="text-xs text-gray-600 mt-0.5 max-w-md">{kind.hint}</div>
          </div>
        </div>
        {state && <StatusBadge status={state.status} />}
      </div>
      <div className="mt-4 flex-1 rounded-xl bg-white/80 backdrop-blur border border-gray-200 p-6 text-center">
        <div className="text-sm text-gray-500 mb-3">Full editor lives on its own page.</div>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Link to={`/admin/projects/${project.id}/case-study`} className="inline-flex items-center gap-2 rounded-full bg-gray-900 text-white text-xs font-semibold px-4 py-2 hover:bg-black">
            Open editor
          </Link>
          <a href={pdfUrl(`/admin/projects/${project.id}/case-study.pdf`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-gray-900 text-xs font-semibold px-4 py-2 hover:bg-amber-300" data-testid="case-study-view-pdf">
            View PDF
          </a>
        </div>
      </div>
      <MetaRow state={state} />
      <div className="mt-4 flex flex-wrap gap-2">
        {isAdmin && state?.status !== "approved" && (
          <button onClick={approve} disabled={busy === "approve"} className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white text-xs font-semibold px-4 py-2 hover:bg-emerald-700 disabled:opacity-60" data-testid="output-approve-case_study">
            <ShieldCheck className="h-4 w-4" /> Approve
          </button>
        )}
        <StatusSelect value={state?.status || "draft"} onChange={setStatus} />
      </div>
    </div>
  );
}

const TITLE_CATEGORY_META = {
  technical: "Technical",
  outcome: "Outcome-led",
  location: "Location-led",
  client: "Client-led",
  news: "News-style",
  suggested: "Suggested",
};

function TitlesPanel({ project, onProject }) {
  const [busy, setBusy] = useState(false);
  const suggestions = project?.title_suggestions || {};
  const generated = suggestions.generated || [];
  const favourites = suggestions.favourites || [];

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/projects/${project.id}/generate-titles`);
      onProject({ ...project, title_suggestions: data.title_suggestions });
      toast.success(`${(data.titles || []).length} titles suggested`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Title generation failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleFav = async (title) => {
    try {
      const { data } = await api.post(`/admin/projects/${project.id}/titles/favourite`, { title });
      onProject({ ...project, title_suggestions: data.title_suggestions });
    } catch {
      toast.error("Could not update favourites");
    }
  };

  const copyTitle = async (title) => {
    await navigator.clipboard.writeText(title);
    toast.success("Title copied");
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-violet-400/15 to-violet-500/5 p-5 sm:p-6" data-testid="titles-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/70 backdrop-blur inline-flex items-center justify-center border border-white/60">
            <Type className="h-5 w-5 text-gray-800" />
          </div>
          <div>
            <div className="font-display font-semibold text-gray-900">Suggested Titles</div>
            <div className="text-xs text-gray-600 mt-0.5 max-w-md">
              10 categorized options — technical, outcome, location, client and news-style. Star your favourites.
            </div>
          </div>
        </div>
        <button
          onClick={generate}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-gray-900 text-white text-xs font-semibold px-4 py-2 hover:bg-black disabled:opacity-60"
          data-testid="titles-generate"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {generated.length ? "Generate More Titles" : "Suggest Titles"}
        </button>
      </div>

      {favourites.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-widest text-violet-700 font-bold mb-2">Favourites</div>
          <div className="space-y-1.5">
            {favourites.map((t) => (
              <div key={t} className="flex items-center gap-2 rounded-lg bg-white/80 border border-violet-200 px-3 py-2 text-sm" data-testid="favourite-title-row">
                <Star className="h-4 w-4 text-amber-500 fill-amber-400 shrink-0" />
                <span className="flex-1">{t}</span>
                <button onClick={() => copyTitle(t)} className="p-1 rounded hover:bg-gray-100"><Copy className="h-3.5 w-3.5 text-gray-500" /></button>
                <button onClick={() => toggleFav(t)} className="p-1 rounded hover:bg-gray-100 text-xs text-gray-400">✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {generated.length > 0 && (
        <div className="mt-4 grid sm:grid-cols-2 gap-1.5">
          {generated.map((t, i) => {
            const isFav = favourites.includes(t.text);
            return (
              <div key={`${t.text}-${i}`} className="flex items-center gap-2 rounded-lg bg-white/70 border border-gray-200 px-3 py-2 text-sm" data-testid="suggested-title-row">
                <span className="text-[9px] uppercase tracking-wider bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full font-bold shrink-0">
                  {TITLE_CATEGORY_META[t.category] || t.category}
                </span>
                <span className="flex-1 leading-snug">{t.text}</span>
                <button onClick={() => copyTitle(t.text)} className="p-1 rounded hover:bg-gray-100" title="Copy">
                  <Copy className="h-3.5 w-3.5 text-gray-500" />
                </button>
                <button onClick={() => toggleFav(t.text)} className="p-1 rounded hover:bg-gray-100" title="Favourite" data-testid={`title-fav-${i}`}>
                  <Star className={`h-3.5 w-3.5 ${isFav ? "text-amber-500 fill-amber-400" : "text-gray-400"}`} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminProjectOutputs() {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [outputs, setOutputs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [pRes, oRes] = await Promise.all([
          api.get(`/admin/projects/${id}`),
          api.get(`/admin/projects/${id}/outputs`),
        ]);
        if (!mounted) return;
        setProject(pRes.data);
        setOutputs(oRes.data.outputs || {});
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  const title = useMemo(() => {
    if (!project) return "";
    return project.project_name
      || project?.type_data?.award_name
      || project?.type_data?.certification_name
      || project?.type_data?.event_name
      || project?.type_data?.title
      || "Untitled";
  }, [project]);

  const onState = (kindKey, next) => {
    setOutputs((prev) => ({ ...prev, [kindKey]: next }));
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-5 sm:p-8">
        <Link to={`/admin/projects/${id}`} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-3">
          <ArrowLeft className="h-4 w-4" /> Back to project
        </Link>
        <div className="mb-6 sm:mb-8">
          <div className="text-xs uppercase tracking-widest text-amber-600 font-bold">Outputs</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight leading-tight mt-1">
            Reuse this record<span className="text-amber-500">.</span>
          </h1>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            One captured record → a full content pack: article, platform-specific social
            posts, bid and award evidence. Every generation creates a new version — earlier
            versions are always kept, and nothing publishes without approval.
          </p>
          {project && (
            <div className="mt-3 text-sm text-gray-500">
              Source: <span className="font-semibold text-gray-800">{title}</span>
              {project.entry_type && <span className="ml-2 text-xs uppercase tracking-widest bg-gray-100 text-gray-700 rounded-full px-2 py-1">{project.entry_type}</span>}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-24">
            <Loader2 className="h-6 w-6 animate-spin inline" />
          </div>
        ) : (
          <div className="space-y-8">
            <TitlesPanel project={project} onProject={setProject} />
            <div>
              <h2 className="font-display text-lg font-semibold text-gray-900 mb-3">Case Study</h2>
              <CaseStudyCard project={project} state={outputs.case_study} onState={onState} isAdmin={isAdmin} />
            </div>
            {SECTIONS.map((section) => (
              <div key={section.title}>
                <h2 className="font-display text-lg font-semibold text-gray-900 mb-3">{section.title}</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {section.keys.map((key) => {
                    const k = KINDS.find((x) => x.key === key);
                    return (
                      <OutputCard
                        key={key}
                        projectId={id}
                        kind={k}
                        state={outputs[key]}
                        onState={onState}
                        isAdmin={isAdmin}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
